import { Component, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Api } from 'src/app/services/api';
import { Globals } from 'src/app/services/globals';
import { removePadding, slugifyName } from '../../../../../../../common/common-helpers/utility';
import { IPoster } from '../../../../../../../common/common-interfaces/poster';
import { IPrintFile } from '../../../../../../../common/common-interfaces/print-file';
import { canvasSizesByName, posterSizesByName } from '../../../../../../../common/common-types/poster-size';
import { size } from 'lodash';
import { availableTShirtSizes } from '../../../../../../../common/common-third-party/exakta';
import { availableExaktaTShirtSKUs } from '../../../../../../../common/common-third-party/exakta/skus/available';

/**
 * Mockups are labelled by what they show, since a poster with passepartout
 * variants has two that look alike at thumbnail size.
 */
const MOCKUP_LABELS: { [type: string]: string } = {
  'poster': 'Mockup',
  'canvas': 'Mockup',
  'poster-with-passepartout': 'Mockup med passepartout'
};

@Component({
    selector: 'poster',
    templateUrl: './poster.component.html',
    styleUrls: ['./poster.component.scss'],
    standalone: false
})
export class PosterComponent {
  posterId?: string;
  poster: IPoster;
  printFiles?: IPrintFile[];

  prompt: string = '';

  printFilePreviews: { [posterType: string]: any[] } = {};
  private paramsSubscription: Subscription | null = null;


  missingSizes: { [posterType: string]: string[] } = {};
  selectedSize?: string;
  selectedType?: string = 'POSTER';
  thumbnailUrl?: SafeResourceUrl;
  /** The mounted thumbnail used for the Shopify passepartout variants. */
  passepartoutThumbnailUrl?: string;
  /** Generated interior mockups published as extra Shopify product images. */
  mockupImages: { label: string, url: string }[] = [];

  generatingDescription: boolean = false;
  generatingPrintFile: boolean = false;


  shopifySizesToCreate: {
    poster: { [size: string]: boolean },
    canvas: { [size: string]: boolean }
  } = {
      canvas: {},
      poster: {}
    }
  availablePosterSizes = Object.keys(posterSizesByName);
  availableCanvasSizes = Object.keys(canvasSizesByName);

  /** Details of the connected Shopify product, loaded lazily for the Shopify tab. */
  shopifyProduct: any = null;
  loadingShopifyProduct = false;
  availableTShirtSizes = availableTShirtSizes;
  availableTShirtSKUs = availableExaktaTShirtSKUs;

  barcode: string = '';
  sku: string = '';
  constructor(private api: Api, private router: Router, private route: ActivatedRoute, public globals: Globals, private sanitizer: DomSanitizer) {

  }

  async ngOnInit() {

    this.paramsSubscription = this.route.params.subscribe(async (params) => {
      if (params['id']) {
        this.posterId = params['id'];

        await this.refresh();
      }
    });
  }

  async refresh() {
    this.poster = await this.api.store().get<any>(`/posters/${this.posterId}`);

    if (!this.poster.metadata)
      this.poster.metadata = {};


    this.printFiles = await this.api.store().get<any>(`/posters/${this.posterId}/print-files`);
    let sizes: { [posterType: string]: { [size: string]: boolean } } = {
      POSTER: {},
      CANVAS: {},
      TSHIRT: {}
    };
    this.availablePosterSizes.forEach(s => sizes['POSTER'][s] = true);
    this.availableCanvasSizes.forEach(s => sizes['CANVAS'][s] = true);
    this.availableTShirtSKUs.forEach(s => sizes['TSHIRT'][s] = true);

    /*
    for (let size in this.poster.printFiles) {
      let printFile = await this.api.store().get<any>(`/print-files/${this.poster.printFiles[size].printFileId}`);
      delete sizes[size]
      this.printFilePreviews.push({
        size: size,
        url: this.sanitizer.bypassSecurityTrustResourceUrl(`/api/stores/by-access-code/${this.globals.accessCode}/admin/print-files/${this.poster.printFiles[size].printFileId}/data`),
        printFile: printFile
      });
    }
*/

    for (let type in this.poster.printFilesByType) {
      if (!this.printFilePreviews[type])
        this.printFilePreviews[type] = [];
      for (let size in this.poster.printFilesByType[type]) {
        let printFile = await this.api.store().get<any>(`/print-files/${this.poster.printFilesByType[type][size].printFileId}`);
        if (sizes[type])
          delete sizes[type][size];
        this.printFilePreviews[type].push({
          size: size,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(`/api/stores/by-access-code/${this.globals.accessCode}/admin/print-files/${this.poster.printFilesByType[type][size].printFileId}/data`),
          printFile: printFile
        });
      }
    }

    this.missingSizes = {};
    for (let type in sizes) {
      this.missingSizes[type] = Object.keys(sizes[type]);
    }

    if (this.poster.thumbnail)
      this.thumbnailUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.poster.thumbnail.url);

    // Reset first: refresh() re-runs this, and these would otherwise accumulate.
    this.passepartoutThumbnailUrl = null;
    this.mockupImages = [];

    // Plain strings rather than sanitizer-bypassed values: these are bucket
    // https URLs that Angular's own sanitizer passes through, and an <a href>
    // will not take a SafeResourceUrl.
    if (this.poster.additionalThumbnails && this.poster.additionalThumbnails.passepartout)
      this.passepartoutThumbnailUrl = this.poster.additionalThumbnails.passepartout.url;

    for (let mockup of this.poster.mockupImages || []) {
      if (mockup && mockup.image && mockup.image.url)
        this.mockupImages.push({ label: MOCKUP_LABELS[mockup.type] || 'Mockup', url: mockup.image.url });
    }

    this.shopifySizesToCreate.poster['21x30'] = true;
    this.shopifySizesToCreate.poster['30x40'] = true;
    this.shopifySizesToCreate.poster['50x70'] = true;
    this.shopifySizesToCreate.poster['70x100'] = true;


    this.shopifySizesToCreate.canvas['30x40'] = true;
    this.shopifySizesToCreate.canvas['50x70'] = true;
    this.shopifySizesToCreate.canvas['70x100'] = true;

    await this.loadShopifyProduct();
  }

  /**
   * Example of the variant SKUs that will be created, so the format is visible
   * before publishing. Mirrors the server: PRODUCT_SIZE_<poster sku>.
   */
  shopifySkuPreview() {
    let sku = this.poster && this.poster.sku
      ? this.poster.sku
      : slugifyName(this.poster ? this.poster.name : '');

    let firstPoster = this.availablePosterSizes.find(s => this.shopifySizesToCreate.poster[s]);
    let firstCanvas = this.availableCanvasSizes.find(s => this.shopifySizesToCreate.canvas[s]);

    let examples: string[] = [];

    if (firstPoster)
      examples.push(`POSTER_${firstPoster}_${sku}`);

    if (firstCanvas)
      examples.push(`CANVAS_${firstCanvas}_${sku}`);

    return examples.join(', ') || `POSTER_50x70_${sku}`;
  }

  /** Loads the connected Shopify product, if there is one. */
  async loadShopifyProduct() {
    if (!this.poster || !this.poster.thirdParty || !this.poster.thirdParty.shopify || !this.poster.thirdParty.shopify.productId) {
      this.shopifyProduct = null;
      return;
    }

    this.loadingShopifyProduct = true;

    try {
      let result = await this.api.store().get<any>(`/posters/${this.posterId}/shopify-product`);
      this.shopifyProduct = result && result.connected ? result : null;
    }
    catch (e) {
      console.log(e);
      this.shopifyProduct = null;
    }
    finally {
      this.loadingShopifyProduct = false;
    }
  }

  /**
   * Admin link for the connected product. Prefers the URL the server built, and
   * falls back to composing it from the shop name.
   */
  shopifyProductAdminUrl() {
    if (this.shopifyProduct && this.shopifyProduct.adminUrl)
      return this.shopifyProduct.adminUrl;

    let shopifySettings = this.globals.storeSettings ? this.globals.storeSettings.shopifySettings : null;
    let productId = this.poster && this.poster.thirdParty && this.poster.thirdParty.shopify
      ? this.poster.thirdParty.shopify.productId
      : null;

    if (!shopifySettings || !productId)
      return null;

    return `https://admin.shopify.com/store/${shopifySettings.shopName.replace('.myshopify.com', '')}/products/${productId}`;
  }

  barcodeChanged(e) {
    this.barcode = e;
    let barcodeParts = this.barcode.split('-');
    this.sku = `${this.poster._id}`;

  }



  async printFileUploaded(data: any) {
    delete this.selectedSize;
    this.refresh();
  }

  async save() {
    await this.api.store().patch<any>(`/posters/${this.posterId}`, this.poster);
  }

  async generateDescription() {
    this.generatingDescription = true;
    let result = await this.api.store().post<any>(`/third-party/openai/generate-text`, { prompt: this.prompt });
    this.poster.description = result.result;
    this.generatingDescription = false;
  }

  async generatePrintFile() {
    this.generatingPrintFile = true;
    this.poster = await this.api.store().post<any>(`/posters/${this.posterId}/generate-print-files`, {
      sizes: [this.selectedSize]
    });
    this.generatingPrintFile = false;
  }

  async createShopifyProduct() {
    let result = await this.api.store().post<any>(`/posters/${this.posterId}/create-shopify-product`, {
      sizes: {
        poster: Object.keys(this.shopifySizesToCreate.poster).filter(s => this.shopifySizesToCreate.poster[s]),
        canvas: Object.keys(this.shopifySizesToCreate.canvas).filter(s => this.shopifySizesToCreate.canvas[s])
      }
    });
    if (result) {
      this.poster.thirdParty = result.thirdParty;
      // The server derives a SKU from the name when the poster has none, and the
      // variant SKUs are built from it, so reflect it back in the form.
      this.poster.sku = result.sku;
      await this.loadShopifyProduct();
    }
  }

  async deleteShopifyProduct() {
    let result = await this.api.store().post<any>(`/posters/${this.posterId}/delete-shopify-product`, {});
    if (result) {
      this.poster.thirdParty = result.thirdParty;
      this.shopifyProduct = null;
    }
  }

  async createWoocommerceProduct() {
    let result = await this.api.store().post<any>(`/posters/${this.posterId}/create-woocommerce-product`, {});
    if (result)
      this.poster.thirdParty = result.thirdParty;
  }

  async deleteWoocommerceProduct() {
    let result = await this.api.store().post<any>(`/posters/${this.posterId}/delete-woocommerce-product`, {});
    if (result)
      this.poster.thirdParty = result.thirdParty;
  }

  async autoGenerateDescription() {
    this.generatingDescription = true;
    /*
    let annotateResult = await this.api.store().post<any>(`/third-party/google/vision/annotate-poster/${this.poster._id}`, {});

    let objects = annotateResult.result.object;
    let labels = annotateResult.result.label;

    let prompt = `Skriv en säljande beskrivningstext för en poster som föreställer ${objects.join(', ')} och är taggad med följande: ${labels.join(', ')}`;
    let result = await this.api.store().post<any>(`/third-party/openai/generate-text`, { prompt: prompt });
    this.poster.description = result.result;*/


    let result = await this.api.store().get<any>(`/posters/${this.posterId}/auto-generated-description`);
    this.poster.description = result.description;
    this.poster.metadata.labels = result.labels;
    this.poster.metadata.objects = result.objects;

    this.generatingDescription = false;
  }


  ngOnDestroy(): void {
    (this.paramsSubscription as Subscription).unsubscribe();

  }

}


